import React, { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { reviews } from '../../mock';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Reveal } from '../motion/Reveal';
import { useMediaQuery } from '../../hooks/useIsMobile';
import { useSwipe } from '../../hooks/useSwipe';

export const Reviews = () => {
  const { t } = useLanguage();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
  const perPage = isMobile ? 1 : isTablet ? 2 : 3;
  const totalPages = Math.ceil(reviews.length / perPage);
  const [page, setPage] = useState(0);

  useEffect(() => setPage(0), [perPage]);

  useEffect(() => {
    const timer = setInterval(() => setPage((prev) => (prev + 1) % totalPages), 5000);
    return () => clearInterval(timer);
  }, [totalPages]);

  const next = () => setPage((prev) => (prev + 1) % totalPages);
  const prev = () => setPage((prev) => (prev - 1 + totalPages) % totalPages);
  const swipe = useSwipe(next, prev);

  return (
    <section className="py-20 md:py-24 bg-[#1a1a1a]">
      <div className="container mx-auto px-5 md:px-10">
        <Reveal className="text-center mb-12 md:mb-16">
          <h2 className="heading-4 mb-4 gradient-text-primary">
            {t({ sl: 'KAJ PRAVIJO STRANKE', en: 'WHAT CUSTOMERS SAY', it: 'COSA DICONO I CLIENTI' })}
          </h2>
          <div className="flex items-center justify-center gap-2 mb-4">
            {[...Array(5)].map((_, i) => (
              <Star key={`hero-star-${i}`} className="w-6 h-6 fill-[white] text-[white]" />
            ))}
            <span className="text-[white] ml-2 font-semibold">4.9 / 5</span>
          </div>
          <p className="text-[#b0b0b0] body-small">{t({ sl: 'Iz 30 ocen', en: 'From 30 reviews', it: 'Da 30 recensioni' })}</p>
        </Reveal>

        <Reveal className="relative" delay={0.1}>
          <button onClick={prev} className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 rounded-full p-3 transition-colors -ml-4" aria-label="Previous reviews" data-testid="reviews-prev">
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          <button onClick={next} className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 rounded-full p-3 transition-colors -mr-4" aria-label="Next reviews" data-testid="reviews-next">
            <ChevronRight className="w-6 h-6 text-white" />
          </button>

          <div className="overflow-hidden md:px-8" {...swipe} data-testid="reviews-carousel">
            <div className="flex transition-transform duration-500 ease-in-out" style={{ transform: `translateX(-${page * 100}%)` }}>
              {[...Array(totalPages)].map((_, pageIndex) => (
                <div key={`review-page-${pageIndex}`} className="flex gap-6 flex-shrink-0 w-full justify-center">
                  {reviews.slice(pageIndex * perPage, (pageIndex + 1) * perPage).map((review) => (
                    <Card key={review.id} className="bg-[#000000] border-[rgba(255,255,255,0.1)] w-full md:w-[350px] flex-shrink-0" data-testid={`review-card-${review.id}`}>
                      <CardHeader>
                        <div className="flex items-center gap-1 mb-2">
                          {[...Array(review.rating)].map((_, i) => (
                            <Star key={`${review.id}-star-${i}`} className="w-4 h-4 fill-[white] text-[white]" />
                          ))}
                        </div>
                        <CardTitle className="text-[white] text-base">{review.name}</CardTitle>
                        <CardDescription className="text-[#b0b0b0] text-sm">{t(review.service)}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-[white] body-small italic">&ldquo;{t(review.text)}&rdquo;</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-center gap-4 mt-8">
            <button onClick={prev} className="md:hidden w-11 h-11 flex items-center justify-center bg-white/10 rounded-full" aria-label="Previous reviews" data-testid="reviews-prev-mobile">
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <div className="flex gap-2">
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={`review-dot-${index}`}
                  onClick={() => setPage(index)}
                  className={`h-2.5 rounded-full transition-all ${index === page ? 'bg-white w-6' : 'bg-white/30 w-2.5'}`}
                  aria-label={`Reviews page ${index + 1}`}
                />
              ))}
            </div>
            <button onClick={next} className="md:hidden w-11 h-11 flex items-center justify-center bg-white/10 rounded-full" aria-label="Next reviews" data-testid="reviews-next-mobile">
              <ChevronRight className="w-5 h-5 text-white" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
